import * as React from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { addTileGlyphRecipe, addTileRecipe } from '../recipes/mobile.recipe';
import { Icon, type IconComponent } from './Icon';

export interface AddTileProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** What it adds, as a verb: "Add photos", "Add vehicle", "Add note". */
  children: React.ReactNode;
  /** `block` spans the section, as photos do; `inline` hugs its words. */
  width?: 'block' | 'inline';
  /** `solid` for a choice still to make, the create sheet's operator. */
  appearance?: 'dashed' | 'solid';
  /**
   * A block 64 high rather than 88, for one inside a form or under a
   * camera: the vehicle sheet's Scan VIN.
   */
  compact?: boolean;
  /**
   * A glyph that says what is being added, drawn large in place of the
   * small plus: Users for "Add an operator".
   */
  icon?: IconComponent;
}

/**
 * The way in to something a call does not have yet: the Figma's dashed
 * \"Add photos\", \"Add vehicle\" and \"Add note\" tiles, which stand where
 * the photos, the vehicle or the notes will be.
 *
 *   <AddTile width="block" onClick={() => setAdding(true)}>Add photos</AddTile>
 *
 * Once there is something, the section shows it with its own add button;
 * this is only for the empty case, where the whole section is the button.
 */
export const AddTile = React.forwardRef<HTMLButtonElement, AddTileProps>(
  function AddTile(
    {
      width = 'inline',
      appearance,
      compact,
      icon,
      className,
      children,
      ...props
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(addTileRecipe({ width, appearance, compact }), className)}
        {...props}
      >
        {icon ? (
          <Icon icon={icon} size="xl" />
        ) : (
          <span className={addTileGlyphRecipe()} aria-hidden="true">
            <Plus />
          </span>
        )}
        {children}
      </button>
    );
  }
);
